import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './BikeOfTheWeek.css';

const CATEGORIES = ['street_legal','mini_moto','medium_moto','mega_moto'];
const CAT_LABELS  = { street_legal:'Street Legal', mini_moto:'Mini Moto', medium_moto:'Medium Moto', mega_moto:'Mega Moto' };

function getNextSunday() {
  const now = new Date();
  const day = now.getDay();
  const daysUntil = day === 0 ? 7 : 7 - day;
  const next = new Date(now);
  next.setDate(now.getDate() + daysUntil);
  next.setHours(0, 0, 0, 0);
  return next;
}

function getWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
}

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const tick = () => {
      const diff = getNextSunday() - new Date();
      if (diff <= 0) { setTimeLeft({ d:0,h:0,m:0,s:0 }); return; }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

export default function BikeOfTheWeek() {
  const { user } = useAuth();
  const countdown = useCountdown();

  const [submissions, setSubmissions]   = useState([]);
  const [leaders,     setLeaders]       = useState({});
  const [userVotes,   setUserVotes]     = useState({});
  const [userSub,     setUserSub]       = useState(null); // user's own submission this week
  const [showForm,    setShowForm]      = useState(false);
  const [loading,     setLoading]       = useState(true);
  const [submitting,  setSubmitting]    = useState(false);
  const [deleting,    setDeleting]      = useState(false);
  const [error,       setError]         = useState('');
  const [success,     setSuccess]       = useState('');
  const [activeTab,   setActiveTab]     = useState('street_legal');

  const [form, setForm] = useState({
    rider_name: '', bike_name: '', category: 'street_legal',
    stage: '1', description: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const weekStart = getWeekStart();

    const { data: subs } = await supabase
      .from('botw_submissions')
      .select('*, botw_votes(count)')
      .gte('created_at', weekStart.toISOString())
      .order('created_at', { ascending: false });

    if (subs) {
      setSubmissions(subs);

      // Find user's own submission this week
      if (user) {
        const mine = subs.find(s => s.user_id === user.id);
        setUserSub(mine || null);
      }

      const top = {};
      CATEGORIES.forEach(cat => {
        const catSubs = subs.filter(s => s.category === cat);
        if (catSubs.length > 0) {
          top[cat] = catSubs.reduce((a, b) =>
            (b.botw_votes?.[0]?.count || 0) > (a.botw_votes?.[0]?.count || 0) ? b : a
          );
        }
      });
      setLeaders(top);
    }

    if (user) {
      const { data: votes } = await supabase
        .from('botw_votes')
        .select('submission_id')
        .eq('user_id', user.id)
        .gte('created_at', weekStart.toISOString());
      if (votes) {
        const map = {};
        votes.forEach(v => { map[v.submission_id] = true; });
        setUserVotes(map);
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!user) return;

    // Enforce one submission per week
    if (userSub) {
      setError('You already have a submission this week. Delete it first to resubmit.');
      return;
    }

    setSubmitting(true); setError(''); setSuccess('');

    try {
      let imageUrl = '';
      if (imageFile) {
        const ext  = imageFile.name.split('.').pop();
        const path = `botw/${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('listing-images').upload(path, imageFile);
        if (upErr) throw upErr;
        const { data } = supabase.storage.from('listing-images').getPublicUrl(path);
        imageUrl = data.publicUrl;
      }

      const { error: insErr } = await supabase.from('botw_submissions').insert({
        user_id:     user.id,
        rider_name:  form.rider_name,
        bike_name:   form.bike_name,
        category:    form.category,
        stage:       parseInt(form.stage),
        description: form.description,
        image_url:   imageUrl,
      });
      if (insErr) throw insErr;

      setSuccess('Your bike has been submitted!');
      setShowForm(false);
      setForm({ rider_name:'', bike_name:'', category:'street_legal', stage:'1', description:'' });
      setImageFile(null); setImagePreview('');
      fetchData();
    } catch (err) {
      setError(err.message || 'Submission failed.');
    }
    setSubmitting(false);
  }

  async function handleDelete(submissionId) {
    if (!window.confirm('Delete your submission? This cannot be undone.')) return;
    setDeleting(true);
    const { error } = await supabase.from('botw_submissions').delete().eq('id', submissionId);
    if (error) {
      setError('Failed to delete: ' + error.message);
    } else {
      setSuccess('Submission deleted. You can submit a new bike!');
      setUserSub(null);
      fetchData();
    }
    setDeleting(false);
    setTimeout(() => setSuccess(''), 4000);
  }

  async function handleVote(submissionId, category) {
    if (!user) return;
    if (userVotes[submissionId]) return;

    const catSubs = submissions.filter(s => s.category === category);
    const alreadyVotedInCat = catSubs.some(s => userVotes[s.id]);

    if (alreadyVotedInCat) {
      setError('You already voted in this category this week.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const { error } = await supabase.from('botw_votes').insert({
      submission_id: submissionId,
      user_id: user.id,
    });

    if (!error) {
      setUserVotes(prev => ({ ...prev, [submissionId]: true }));
      fetchData();
    }
  }

  const catSubmissions = submissions.filter(s => s.category === activeTab);
  const pad = n => String(n).padStart(2, '0');

  return (
    <div className="botw-page page-enter">
      <div className="container">

        {/* Header */}
        <div className="botw-header">
          <div>
            <p className="botw-eyebrow mono">Weekly Competition</p>
            <h1 className="botw-title">Bike of the Week</h1>
            <p className="botw-sub">Submit your build. Vote for your favorite. Winner resets every Sunday.</p>
          </div>
          <div className="botw-countdown">
            <p className="countdown-label mono">Resets in</p>
            <div className="countdown-timer">
              <div className="countdown-unit"><span className="countdown-num">{pad(countdown.d)}</span><span className="countdown-unit-label">Days</span></div>
              <span className="countdown-sep">:</span>
              <div className="countdown-unit"><span className="countdown-num">{pad(countdown.h)}</span><span className="countdown-unit-label">Hrs</span></div>
              <span className="countdown-sep">:</span>
              <div className="countdown-unit"><span className="countdown-num">{pad(countdown.m)}</span><span className="countdown-unit-label">Min</span></div>
              <span className="countdown-sep">:</span>
              <div className="countdown-unit"><span className="countdown-num">{pad(countdown.s)}</span><span className="countdown-unit-label">Sec</span></div>
            </div>
          </div>
        </div>

        {/* Current Leaders */}
        {Object.keys(leaders).length > 0 && (
          <div className="leaders-section">
            <p className="section-label mono">Current Leaders</p>
            <div className="leaders-grid">
              {CATEGORIES.map(cat => leaders[cat] && (
                <div key={cat} className="leader-card">
                  <div className="leader-crown">No.1</div>
                  {leaders[cat].image_url
                    ? <img src={leaders[cat].image_url} alt={leaders[cat].bike_name} className="leader-img" />
                    : <div className="leader-img-placeholder"><span>{CAT_LABELS[cat][0]}</span></div>
                  }
                  <div className="leader-info">
                    <p className="leader-cat mono">{CAT_LABELS[cat]}</p>
                    <p className="leader-bike">{leaders[cat].bike_name}</p>
                    <p className="leader-rider">by {leaders[cat].rider_name}</p>
                    <p className="leader-votes">{leaders[cat].botw_votes?.[0]?.count || 0} votes</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts */}
        {error   && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Submit / My Submission actions */}
        <div className="botw-actions">
          {!user ? (
            <Link to="/login" className="btn btn-primary">Log In to Submit</Link>
          ) : userSub ? (
            <div className="my-sub-banner">
              <span>✅ You've submitted <strong>{userSub.bike_name}</strong> this week</span>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(userSub.id)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete My Submission'}
              </button>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : 'Submit My Bike'}
            </button>
          )}
        </div>

        {/* Submission form */}
        {showForm && !userSub && (
          <div className="botw-form-card">
            <h3>Submit Your Bike</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input className="form-input" placeholder="Rider name" value={form.rider_name} onChange={e => setForm(p => ({...p, rider_name: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Bike Name *</label>
                  <input className="form-input" placeholder="e.g. 2023 Sur-Ron Ultra" value={form.bike_name} onChange={e => setForm(p => ({...p, bike_name: e.target.value}))} required />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Build Stage *</label>
                  <select className="form-select" value={form.stage} onChange={e => setForm(p => ({...p, stage: e.target.value}))}>
                    <option value="1">Stage 1 — Stock</option>
                    <option value="2">Stage 2 — Basic</option>
                    <option value="3">Stage 3 — Dialed</option>
                    <option value="4">Stage 4 — Built</option>
                    <option value="5">Stage 5 — Tank</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" placeholder="Tell us about your build..." value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Photo *</label>
                <label className="botw-upload">
                  <input type="file" accept="image/*" onChange={handleImageChange} hidden required />
                  {imagePreview
                    ? <img src={imagePreview} alt="preview" className="botw-preview" />
                    : <div className="botw-upload-placeholder"><span>+</span><span>Upload Photo</span></div>
                  }
                </label>
              </div>
              <button type="submit" className="btn btn-primary" style={{width:'100%'}} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Bike'}
              </button>
            </form>
          </div>
        )}

        {/* Category tabs */}
        <div className="botw-tabs">
          {CATEGORIES.map(cat => (
            <button key={cat} className={`botw-tab ${activeTab === cat ? 'active' : ''}`} onClick={() => setActiveTab(cat)}>
              {CAT_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Submissions grid */}
        {loading ? <div className="spinner" /> : catSubmissions.length === 0 ? (
          <div className="empty-state">
            <h3>No submissions yet</h3>
            <p>Be the first to submit a {CAT_LABELS[activeTab]} this week.</p>
          </div>
        ) : (
          <div className="botw-grid">
            {catSubmissions
              .sort((a,b) => (b.botw_votes?.[0]?.count||0) - (a.botw_votes?.[0]?.count||0))
              .map((sub, i) => (
              <div key={sub.id} className={`botw-card ${i === 0 ? 'top' : ''} ${sub.user_id === user?.id ? 'my-card' : ''}`}>
                {i === 0 && <div className="botw-rank-badge">No.1 This Week</div>}
                {sub.user_id === user?.id && <div className="botw-my-badge">Your Submission</div>}
                <div className="botw-card-img">
                  {sub.image_url
                    ? <img src={sub.image_url} alt={sub.bike_name} />
                    : <div className="botw-no-img"><span>{sub.bike_name[0]}</span></div>
                  }
                </div>
                <div className="botw-card-body">
                  <div className="botw-card-top">
                    <div>
                      <p className="botw-card-bike">{sub.bike_name}</p>
                      <p className="botw-card-rider">by {sub.rider_name}</p>
                    </div>
                    <span className="badge badge-violet">Stage {sub.stage}</span>
                  </div>
                  {sub.description && <p className="botw-card-desc">{sub.description}</p>}
                  <div className="botw-card-footer">
                    <span className="botw-votes mono">{sub.botw_votes?.[0]?.count || 0} votes</span>
                    {sub.user_id === user?.id ? (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(sub.id)}
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                    ) : user ? (
                      <button
                        className={`btn btn-sm ${userVotes[sub.id] ? 'btn-ghost voted' : 'btn-outline'}`}
                        onClick={() => handleVote(sub.id, sub.category)}
                        disabled={userVotes[sub.id]}
                      >
                        {userVotes[sub.id] ? 'Voted' : 'Vote'}
                      </button>
                    ) : (
                      <Link to="/login" className="btn btn-outline btn-sm">Vote</Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
